// Roster status model (design: "Instructor Roster FF050").
//
// While a class runs, a student is on track / behind / at risk (or signed in to the live session). Once every
// session has happened the roster turns into a sign-off queue: ready to sign off, failed, or still in progress.
// Signed-off students are always "signed".

export const PASS_PERCENT = 70;
export const ATTENDANCE_BEHIND_PERCENT = 75;

/** Live-class results; a roster made only of these is "in progress". */
export const LIVE_RESULTS = ["signed-in", "on-track", "behind", "at-risk"];

export const RESULT_LABEL = {
  signed: "Signed off",
  ready: "Passed · Ready to sign off",
  failed: "Failed",
  progress: "In progress",
  "signed-in": "Signed in",
  "on-track": "On Track",
  behind: "Behind",
  "at-risk": "At Risk",
};

/** Sign-off queue sections, in display order: [result, heading, dot colour]. */
export const GROUPS = [
  ["ready", "Ready to sign off", "#1d4ed8"],
  ["failed", "Failed", "var(--color-danger)"],
  ["progress", "In progress", "var(--color-hairline-cool)"],
  ["signed", "Signed off", "var(--color-success)"],
];

const LIVE_TABS = [
  { key: "all", label: "All" },
  { key: "signed-in", label: "Signed in" },
  { key: "on-track", label: "On Track" },
  { key: "behind", label: "Behind" },
  { key: "at-risk", label: "At Risk" },
];

const WRAP_TABS = [
  { key: "all", label: "All" },
  { key: "ready", label: "Ready to sign off" },
  { key: "signed", label: "Signed off" },
  { key: "failed", label: "Failed" },
  { key: "progress", label: "In progress" },
];

/** Meets every requirement (the server's sign-off rule) and isn't signed off yet. */
export const isReadyToSignOff = (reg) =>
  reg?.eligibleForSignOff === true && reg?.passed !== true && reg?.status !== "WAITLISTED";

const isSigned = (reg) => reg.passed === true || reg.status === "COMPLETED";

/** Every session has taken place, so nothing more can change the student's attendance. */
export const sessionsFinished = (reg) => {
  const total = reg.sessionsTotal ?? 0;
  return total > 0 && (reg.sessionsSoFar ?? 0) >= total;
};

function attendancePercent(reg) {
  const soFar = reg.sessionsSoFar ?? 0;
  return soFar > 0 ? ((reg.sessionsAttended ?? 0) / soFar) * 100 : null;
}

/** Why a student isn't passing, as short phrases. */
export function shortfalls(reg) {
  const out = [];
  if (reg.skillsBadge === "NEEDS_REEVAL") out.push("Skill check needs re-evaluation");
  if (reg.testAvgPercent != null && reg.testAvgPercent < PASS_PERCENT) out.push(`Test below ${PASS_PERCENT}%`);
  const pct = attendancePercent(reg);
  if (pct != null && pct < ATTENDANCE_BEHIND_PERCENT) out.push("Missed a session");
  return out;
}

/**
 * One result per student: signed | ready | failed | progress, or while sessions are still running
 * signed-in | at-risk | behind | on-track.
 */
export function deriveResult(reg, { isSignedIn = false } = {}) {
  if (isSigned(reg)) return "signed";
  if (isReadyToSignOff(reg)) return "ready";
  if (sessionsFinished(reg)) return shortfalls(reg).length > 0 ? "failed" : "progress";
  if (isSignedIn) return "signed-in";
  if (reg.skillsBadge === "NEEDS_REEVAL" || reg.attended === false) return "at-risk";
  const pct = attendancePercent(reg);
  if (pct != null && pct < ATTENDANCE_BEHIND_PERCENT) return "behind";
  return "on-track";
}

/** The line shown under a result pill (why a student failed or is at risk), or "". */
export function resultNote(reg, result) {
  if (result === "failed") return shortfalls(reg).join(" · ");
  if (result === "at-risk") {
    return reg.skillsBadge === "NEEDS_REEVAL" ? "Skill check needs re-evaluation" : "Missed a session";
  }
  return "";
}

/** inflight: nobody has finished yet. complete: everyone is signed off or failed. wrapping: anything between. */
export function deriveMode(results) {
  if (results.length === 0 || results.every((r) => LIVE_RESULTS.includes(r))) return "inflight";
  if (results.every((r) => r === "signed" || r === "failed")) return "complete";
  return "wrapping";
}

/** Filter tabs for the mode; wrap-up tabs with nobody in them are left out (All stays). */
export function filterTabsFor(mode, counts) {
  if (mode === "inflight") return LIVE_TABS;
  return WRAP_TABS.filter((t) => t.key === "all" || (counts[t.key] ?? 0) > 0);
}

/** Sections for the sign-off queue, or null when grouping isn't useful (live class, a filter, a single section). */
export function groupResults(items, mode, filter) {
  if (mode === "inflight" || filter !== "all") return null;
  const groups = GROUPS
    .map(([key, label, color]) => ({ key, label, color, items: items.filter((i) => i.result === key) }))
    .filter((g) => g.items.length > 0);
  return groups.length > 1 ? groups : null;
}
